import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAtom } from "jotai/index";
import moment from "moment";

import { NamedInput } from "components/NamedInput/NamedInput";
import { NamedCheckbox } from "components/NamedCheckbox/NamedCheckbox";
import { NamedSelect } from "components/NamedSelect/NamedSelect";
import { Calendar, Value } from "components/Calendar/Calendar";
import { TextPointer } from "components/TextPointer/TextPointer";
import { RegularText } from "components/RegularText/RegularText";
import { Option } from "components/Select/Select";
import { TimePicker } from "components/TimePicker";

import DaysServices from "utils/days";
import Days from "utils/days";

import {
  EDocumentsModalDataType,
  TDocumentModalData,
} from "../../types/documents";
import {
  openedDocumentAtom,
  openedDocumentStateAtom,
} from "../../atoms/documentsAtom";
import { storesAtom } from "../../atoms/storeAtom";

import styles from "./styles.module.scss";

interface ComponentRenderProps {
  elements: TDocumentModalData[];
}

const ElementContainer = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  return <div className={styles.componentContainer}>{children}</div>;
};

export const ComponentRender: FC<ComponentRenderProps> = ({
  elements,
}): JSX.Element => {
  const [visibleCalendarStart, setVisibleCalendarStart] =
    useState<boolean>(false);
  const [openedDocument, setOpenedDocument] = useAtom(openedDocumentAtom);
  const [openedDocumentState, setOpenedDocumentState] = useAtom(
    openedDocumentStateAtom
  );
  const [stores] = useAtom(storesAtom);

  const [_time, _setTime] = useState<string | null>("");
  const [_date, _setDate] = useState<Value>(new Date());

  const storesOptions: Option[] = useMemo(
    () => stores.map((i) => ({ value: i.storesid, label: i.name })),
    [stores]
  );

  const storeVal: Option | undefined = useMemo(
    () =>
      storesOptions.find(
        (i) => +i.value === Number(openedDocumentState?.storeid)
      ),
    [storesOptions, openedDocumentState.storeid]
  );

  const store2Val: Option | undefined = useMemo(
    () =>
      storesOptions.find(
        (i) => +i.value === Number(openedDocumentState?.store2id)
      ),
    [storesOptions, openedDocumentState.store2id]
  );

  useEffect(() => {
    setOpenedDocumentState({
      time: Days.setDateTime(_date, _time),
    } as any);
  }, [_date, _time]);

  useEffect(() => {
    _setTime(moment(openedDocumentState.time).format("HH:mm:ss"));
    _setDate(openedDocumentState.time as Date);
  }, []);

  return (
    <div className={styles.wrapper}>
      {elements.map(({ type, name, field }) => {
        switch (type) {
          case EDocumentsModalDataType.comment: {
            return (
              <ElementContainer key={name}>
                <NamedInput
                  style={{ width: 500 }}
                  label={name}
                  isLabelRegular
                  value={openedDocumentState.comment || ""}
                  onChange={(ev) =>
                    setOpenedDocumentState({ comment: ev.target.value } as any)
                  }
                />
              </ElementContainer>
            );
          }
          case EDocumentsModalDataType.store: {
            return (
              <ElementContainer key={name}>
                <NamedSelect
                  options={storesOptions}
                  name={name}
                  value={storeVal}
                  onChange={(val) =>
                    setOpenedDocumentState({ storeid: val?.value } as any)
                  }
                  placeholder={"Выберите склад"}
                />
              </ElementContainer>
            );
          }
          case EDocumentsModalDataType.store2: {
            return (
              <ElementContainer key={name}>
                <NamedSelect
                  options={storesOptions}
                  name={name}
                  value={store2Val}
                  onChange={(val) =>
                    setOpenedDocumentState({ store2id: val?.value } as any)
                  }
                  placeholder={"Выберите склад"}
                />
              </ElementContainer>
            );
          }
          case EDocumentsModalDataType.priceupdated: {
            return (
              <ElementContainer key={name}>
                <NamedCheckbox
                  name={name}
                  checked={openedDocumentState.priceupdated}
                  onChange={() =>
                    setOpenedDocumentState({
                      priceupdated: !openedDocumentState.priceupdated,
                    } as any)
                  }
                />
              </ElementContainer>
            );
          }
          case EDocumentsModalDataType.textPointer: {
            return (
              <ElementContainer key={name}>
                <TextPointer
                  text={name}
                  pointer={openedDocumentState[field as string]}
                  secondTextClassName="text-purple-500"
                />
              </ElementContainer>
            );
          }
          case EDocumentsModalDataType.date: {
            return (
              <ElementContainer key={name}>
                <RegularText className="mr-4">Дата:</RegularText>
                <Calendar
                  onShow={() => setVisibleCalendarStart(!visibleCalendarStart)}
                  setVisibleCalendar={setVisibleCalendarStart}
                  visibleCalendar={visibleCalendarStart}
                  onChange={(val) => {
                    _setDate(val);
                  }}
                  value={_date}
                />
              </ElementContainer>
            );
          }
          case EDocumentsModalDataType.time: {
            return (
              <ElementContainer key={name}>
                <TimePicker
                  value={_time}
                  onChange={(val) => _setTime(val)}
                  label={name}
                  containerClassName={styles.timePickerWrapper}
                />
              </ElementContainer>
            );
          }
        }
        return <div></div>;
      })}
    </div>
  );
};
